/**
 * Recipe Metadata Sync Middleware
 *
 * Persists recipe metadata mutations (rating, verdicts, notes, cooked stamp,
 * favorite, viewed/planned counters) to Firestore.
 *
 * Each action publishes ONLY the fields it owns. Writing the whole local
 * snapshot is how one device silently overwrote the other: `recordViewed` fires
 * merely by opening a recipe, so a long-lived tab holding stale metadata would
 * republish a stale `rating` over the partner's fresh one.
 *
 * Every write is recorded in the slice's outbox first and cleared only once
 * Firestore acknowledges it. Before that, a rating had exactly one chance to
 * reach the cloud — an 800ms debounce, a single `setDoc`, and a `catch` that
 * logged — and losing that chance was silent and permanent: the device kept
 * showing the answer, so the loss only surfaced on another device, as "nothing
 * is rated". Locking the phone right after tapping is enough to lose it, and
 * that is exactly when people rate a meal.
 *
 * Three things follow from the outbox:
 *   - a verdict or rating is written IMMEDIATELY, with no debounce at all: it
 *     is one discrete intent, not a stream, and the debounce only ever existed
 *     to coalesce the chatty counters;
 *   - the pending mark is made before any of this file's guards, so a tap that
 *     lands while Firebase auth is still resolving is queued, not dropped;
 *   - anything left in the outbox is retried on the next boot, on reconnect,
 *     and when the tab comes back to the foreground.
 *
 * Routes through the household path when paired; otherwise per-user. See
 * recipeMetadataSyncService for path resolution.
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  recordPlanned,
  recordMade,
  setRating,
  setEaterVerdict,
  clearRating,
  setNotes,
  clearNotes,
  recordMultiplePlanned,
  toggleFavorite,
  setFavorite,
  recordViewed,
  markSyncPending,
  markSyncAcked,
  type RecipeMetadata,
  type PendingSync,
} from './recipeMetadataSlice';
import {
  syncRecipeMetadataToCloud,
  clearEaterVerdictInCloud,
} from '../services/recipeMetadataSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { trackEvent } from '../services/analyticsService';
import type { RootState } from './store';

export const recipeMetadataSyncMiddleware = createListenerMiddleware();

type Dispatch = (action: unknown) => unknown;

/**
 * redux-persist's flush, handed over by store.ts.
 *
 * Registered rather than imported because store.ts imports this file, and the
 * outbox is worthless if it is still sitting in the throttle when the page
 * dies: redux-persist coalesces writes on a 1-second timer, and a rating tapped
 * 200ms before the phone locks falls inside it. Flushing on the tap puts both
 * the answer and its outbox entry on disk in the same breath, so the next boot
 * has something to retry.
 */
let flushPersist: (() => unknown) | null = null;
export const registerPersistFlush = (fn: () => unknown): void => {
  flushPersist = fn;
};

const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
/**
 * Fields waiting to go out for a recipe, unioned across every action that lands
 * inside the debounce window. Without this, `recordViewed` followed by a verdict
 * tap within 800 ms would publish only the verdict and drop `lastViewed` — the
 * second call replaces the timer, so it has to inherit the first call's fields.
 * `null` means "write everything" and, once set, wins for that window.
 */
const pendingFields = new Map<string, Set<string> | null>();
const DEBOUNCE_MS = 800;

type Fields = Array<keyof RecipeMetadata>;

/**
 * What each action is allowed to publish.
 *
 * `clearRating` and `clearNotes` are absent on purpose: neither is dispatched
 * anywhere in the app, and a narrowed write cannot express a deletion (an
 * undefined value is stripped before it reaches Firestore). They fall through
 * to the write-everything default, which is what they did before.
 */
const FIELDS_BY_ACTION: Record<string, Fields> = {
  [recordPlanned.type]: ['timesPlanned', 'lastPlanned'],
  [recordMultiplePlanned.type]: ['timesPlanned', 'lastPlanned'],
  [recordMade.type]: ['lastMade', 'lastMadeBy'],
  [recordViewed.type]: ['lastViewed'],
  [setRating.type]: ['rating'],
  [setEaterVerdict.type]: ['verdicts', 'rating'],
  [setNotes.type]: ['notes', 'hasNotes'],
  [toggleFavorite.type]: ['isFavorite'],
  [setFavorite.type]: ['isFavorite'],
};

/**
 * Actions the user performs once and expects to stick. They skip the debounce
 * entirely — there is nothing to coalesce, and the window is pure exposure.
 */
const IMMEDIATE_ACTIONS = new Set<string>([
  setRating.type,
  setEaterVerdict.type,
  clearRating.type,
]);

/**
 * Every field this file knows how to publish. The outbox stores field names as
 * plain strings (it is persisted), so the retry needs a way back to a typed
 * write without trusting whatever was in localStorage.
 */
const KNOWN_FIELDS = new Set<string>(
  Object.values(FIELDS_BY_ACTION).flat() as string[]
);

/**
 * Push one recipe's outstanding writes to Firestore and clear what lands.
 *
 * Retractions go first and on their own: `setDoc(..., { merge: true })` can add
 * or overwrite a map key but never remove one, so un-tapping a chip needs its
 * own `deleteField` or the answer comes straight back on the next snapshot.
 */
const writeNow = async (
  userId: string,
  recipeId: string,
  entry: PendingSync,
  getState: () => unknown,
  dispatch: Dispatch
): Promise<void> => {
  const state = getState() as RootState;
  const metadata = state.recipeMetadata.metadata[recipeId];
  if (!metadata) {
    // Nothing left to publish. Releasing the entry matters: a write that can
    // never be satisfied would otherwise sit in the outbox forever and be
    // retried on every boot.
    dispatch(
      markSyncAcked({
        recipeId,
        fields: entry.fields,
        clearedEaters: entry.clearedEaters,
      })
    );
    return;
  }
  const householdId = state.auth?.householdId ?? null;

  const cleared = entry.clearedEaters ?? [];
  if (cleared.length > 0) {
    const landed: string[] = [];
    for (const eaterId of cleared) {
      try {
        await clearEaterVerdictInCloud(
          userId,
          recipeId,
          eaterId,
          householdId,
          metadata.rating
        );
        landed.push(eaterId);
      } catch (error) {
        console.error(`Failed to clear verdict on ${recipeId}:`, error);
      }
    }
    if (landed.length > 0) {
      dispatch(markSyncAcked({ recipeId, clearedEaters: landed }));
    }
  }

  const fields = entry.fields.filter((f) => KNOWN_FIELDS.has(f));
  // An entry with no recognised fields still means "write everything", which is
  // what the un-narrowed callers (clearRating, clearNotes) have always done.
  const writeEverything = entry.fields.length === 0 && cleared.length === 0;
  if (fields.length === 0 && !writeEverything) return;

  try {
    await syncRecipeMetadataToCloud(
      userId,
      metadata,
      householdId,
      writeEverything ? undefined : (fields as Fields)
    );
    dispatch(
      markSyncAcked({ recipeId, fields: writeEverything ? entry.fields : fields })
    );
  } catch (error) {
    // Deliberately left in the outbox. The retry passes will try again on the
    // next boot, on reconnect, and when the tab is next foregrounded.
    console.error(`Failed to sync recipe metadata ${recipeId}:`, error);
    // A lost rating used to leave no trace anywhere: no exception, no event,
    // just a device showing an answer nobody else could see. Finding that took
    // reading Firestore against PostHog tap-by-tap. Say it out loud instead.
    trackEvent('recipe_metadata_sync_failed', {
      fields: fields.join(','),
      scope: householdId ? 'household' : 'personal',
      reason: (error as { code?: string })?.code ?? String(error).slice(0, 120),
    });
  }
};

/** What is still owed for a recipe, straight from the persisted outbox. */
const entryFor = (
  getState: () => unknown,
  recipeId: string
): PendingSync | undefined =>
  (getState() as RootState).recipeMetadata.pending?.[recipeId];

const flushRecipe = (
  userId: string,
  recipeId: string,
  fields: Fields | undefined,
  immediate: boolean,
  getState: () => unknown,
  dispatch: Dispatch
) => {
  const existing = debounceTimers.get(recipeId);
  if (existing) clearTimeout(existing);

  const queued = pendingFields.get(recipeId);
  if (!fields || queued === null) {
    pendingFields.set(recipeId, null);
  } else {
    const merged = queued ?? new Set<string>();
    fields.forEach((f) => merged.add(f as string));
    pendingFields.set(recipeId, merged);
  }

  const send = () => {
    debounceTimers.delete(recipeId);
    pendingFields.delete(recipeId);
    const entry = entryFor(getState, recipeId);
    if (!entry) return Promise.resolve();
    return writeNow(userId, recipeId, entry, getState, dispatch);
  };

  if (immediate) {
    void send();
    return;
  }

  debounceTimers.set(recipeId, setTimeout(send, DEBOUNCE_MS));
};

/**
 * Pull the affected recipeId out of any of the slice's action payloads.
 */
const extractRecipeIds = (action: any): string[] => {
  const payload = action.payload;
  if (typeof payload === 'string') return [payload];
  if (Array.isArray(payload)) return payload.filter((x) => typeof x === 'string');
  if (payload && typeof payload === 'object') {
    if (typeof payload.recipeId === 'string') return [payload.recipeId];
  }
  return [];
};

recipeMetadataSyncMiddleware.startListening({
  // hydrateMetadataFromCloud is deliberately NOT here: it is dispatched by the
  // live listener, and matching it would loop write -> snapshot -> write.
  matcher: isAnyOf(
    recordPlanned,
    recordMade,
    setRating,
    setEaterVerdict,
    clearRating,
    setNotes,
    clearNotes,
    recordMultiplePlanned,
    toggleFavorite,
    setFavorite,
    recordViewed
  ),
  effect: async (action, listenerApi) => {
    const dispatch = listenerApi.dispatch as unknown as Dispatch;
    const recipeIds = extractRecipeIds(action);
    const fields = FIELDS_BY_ACTION[action.type];
    const retracted =
      setEaterVerdict.match(action) && action.payload.value === null
        ? action.payload.eaterId
        : undefined;

    // Record the intent BEFORE any guard below can return. A verdict tapped
    // while Firebase auth is still resolving used to be dropped here without a
    // trace; now it waits in the outbox for the retry pass.
    for (const recipeId of recipeIds) {
      dispatch(
        markSyncPending({
          recipeId,
          // A retraction publishes the recomputed rating; the verdict map
          // itself can only be changed by the deleteField call.
          fields: retracted ? ['rating'] : (fields as string[] | undefined),
          clearedEater: retracted,
        })
      );
    }

    const immediate = IMMEDIATE_ACTIONS.has(action.type);

    // Get the answer and its outbox entry onto disk now, before anything else
    // can go wrong. Unthrottled, so it survives a teardown a fraction of a
    // second later; cheap enough because rating is a rare, deliberate action.
    if (immediate && recipeIds.length > 0) {
      try {
        flushPersist?.();
      } catch {
        // A failed flush costs durability against an instant teardown, never
        // the write itself.
      }
    }

    if (!isFirebaseEnabled()) return;

    const state = listenerApi.getState() as RootState;
    const userId = state.auth?.user?.uid;
    if (!userId) return;

    for (const recipeId of recipeIds) {
      flushRecipe(
        userId,
        recipeId,
        fields,
        immediate,
        listenerApi.getState,
        dispatch
      );
    }
  },
});

/**
 * Send everything the outbox is holding, now.
 *
 * Called when the tab is hidden or unloading — the meal plan has had this since
 * #450 and recipe metadata did not, which is exactly why a cooked stamp crossed
 * devices in seconds while the rating behind it never left the phone — and
 * again on boot, on reconnect, and on refocus, to drain whatever an earlier
 * failure left behind.
 */
export const flushPendingRecipeMetadataSync = async (
  getState: () => unknown,
  dispatch: Dispatch
): Promise<void> => {
  if (!isFirebaseEnabled()) return;
  const state = getState() as RootState;
  const userId = state.auth?.user?.uid;
  if (!userId) return;

  for (const timer of debounceTimers.values()) clearTimeout(timer);
  debounceTimers.clear();
  pendingFields.clear();

  const pending = state.recipeMetadata.pending ?? {};
  await Promise.allSettled(
    Object.entries(pending).map(([recipeId, entry]) =>
      writeNow(userId, recipeId, entry, getState, dispatch)
    )
  );
};

export default recipeMetadataSyncMiddleware;
